import { useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Icon, type IconName } from "./icons.js";
import { money } from "../lib/format.js";
import { NO_AUTOFILL, autofillProps } from "../lib/noAutofill.js";

/* Small presentational building blocks. Everything is a plain element with classes from styles.css,
   so the screens read like HTML and the look lives in one place. */

type Variant = "primary" | "secondary" | "ghost" | "danger" | "amber" | "success" | "critical";
type Size = "sm" | "md" | "lg";

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  block,
  busy,
  children,
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; icon?: IconName; block?: boolean; busy?: boolean; ref?: Ref<HTMLButtonElement> }) {
  const cls = ["btn", `btn-${variant}`, size !== "md" ? `btn-${size}` : "", block ? "btn-block" : "", className].filter(Boolean).join(" ");
  return (
    <button type="button" {...rest} className={cls} disabled={rest.disabled || busy}>
      {busy ? <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon, label, size = "md", ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; size?: Size }) {
  return (
    <button type="button" {...rest} className={`btn btn-ghost btn-icon ${size !== "md" ? `btn-${size}` : ""} ${rest.className ?? ""}`} aria-label={label} title={label}>
      <Icon name={icon} />
    </button>
  );
}

export function Field({ label, optional, hint, error, children, className = "" }: { label: string; optional?: boolean; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string }) {
  return (
    <label className={`field ${className}`}>
      <span className="label">
        {label} {optional && <span className="opt">(opcional)</span>}
      </span>
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

export function Input({ className = "", autoComplete, ...rest }: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input {...autofillProps(autoComplete)} {...rest} className={`input ${className}`} />;
}
export function Select({ className = "", ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { ref?: Ref<HTMLSelectElement> }) {
  return <select {...NO_AUTOFILL} {...rest} className={`select ${className}`} />;
}
export function Textarea({ className = "", ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...NO_AUTOFILL} {...rest} className={`textarea ${className}`} />;
}

export function SearchInput({ icon = "search", ...rest }: InputHTMLAttributes<HTMLInputElement> & { icon?: IconName }) {
  return (
    <div className="input-wrap">
      <Icon name={icon} />
      <input type="search" {...NO_AUTOFILL} {...rest} className={`input ${rest.className ?? ""}`} />
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options, block, small }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; block?: boolean; small?: boolean }) {
  return (
    <div className={`seg ${block ? "block" : ""} ${small ? "sm" : ""}`} role="group">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Pill({ on, children, icon, large, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { on: boolean; icon?: IconName; large?: boolean }) {
  return (
    <button type="button" {...rest} className={`pill ${large ? "lg" : ""}`} aria-pressed={on}>
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export function Switch({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="switch">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" />
      <span>{children}</span>
    </label>
  );
}

export type ChipTone = "neutral" | "green" | "amber" | "red" | "blue" | "violet";
export function Chip({ tone = "neutral", children, dot, large }: { tone?: ChipTone; children: ReactNode; dot?: boolean; large?: boolean }) {
  return (
    <span className={`chip ${tone} ${large ? "lg" : ""}`}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}

export function Money({ cents, large, className = "" }: { cents: number; large?: boolean; className?: string }) {
  return <span className={`money ${large ? "lg" : ""} ${className}`}>{money(cents)}</span>;
}

export function Alert({ tone, icon, children }: { tone: "red" | "amber" | "green" | "blue"; icon?: IconName; children: ReactNode }) {
  return (
    <div className={`alert ${tone}`} role={tone === "red" ? "alert" : undefined}>
      <Icon name={icon ?? (tone === "red" || tone === "amber" ? "warning" : tone === "green" ? "check" : "info")} />
      <div>{children}</div>
    </div>
  );
}

export function Empty({ icon = "list", title, hint, children }: { icon?: IconName; title: string; hint?: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <Icon name={icon} />
      <div className="h3">{title}</div>
      {hint && <p className="small">{hint}</p>}
      {children}
    </div>
  );
}

export function Loading({ label = "Cargando…" }: { label?: string }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="spinner lg" />
      <span className="visually-hidden">{label}</span>
    </div>
  );
}

export function PageHead({ title, lede, kicker, actions }: { title: ReactNode; lede?: ReactNode; kicker?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="titles">
        {kicker && <span className="kicker">{kicker}</span>}
        <h1 className="h1">{title}</h1>
        {lede && <p className="lede">{lede}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export function Card({ title, actions, children, footer, className = "", pad = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; footer?: ReactNode; className?: string; pad?: boolean }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-head">
          {typeof title === "string" ? <h2 className="h3">{title}</h2> : title}
          {actions}
        </div>
      )}
      <div className={pad ? "card-body" : ""}>{children}</div>
      {footer && <div className="card-foot">{footer}</div>}
    </section>
  );
}

export function TubeSwatch({ hex, name }: { hex: string | null; name: string | null }) {
  return (
    <span className="tube">
      <span className="tube-cap" style={{ background: hex ?? "#d9d9d9" }} />
      <span>{name ?? "Sin recipiente"}</span>
    </span>
  );
}

/** Native <dialog> as a sheet. Closes on Escape and on backdrop tap. */
export function Sheet({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`sheet ${wide ? "wide" : ""}`}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2 className="h2">{title}</h2>
          <IconButton icon="x" label="Cerrar" onClick={onClose} />
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </dialog>
  );
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: string; count?: number }[] }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((it) => (
        <button key={it.value} role="tab" type="button" aria-selected={it.value === value} onClick={() => onChange(it.value)}>
          {it.label}
          {it.count !== undefined && <span className="faint"> · {it.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Definition({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="definition">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}
